import React, { useState, useEffect } from "react";
import axios from "axios";
import Button from "../common/Button";
import toast from "react-hot-toast";
import { toastOptions } from "../common/toastOptions";

interface MfaSettings {
  mfa_enforced: boolean;
  mfa_allowed_methods: string[];
  mfa_trusted_device_days: number;
  mfa_grace_period_days: number;
  officerMfaStats: {
    total: number;
    configured: number;
  };
}

const MFAConfiguration: React.FC = () => {
  const [settings, setSettings] = useState<MfaSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Local form state
  const [enforced, setEnforced] = useState(false);
  const [allowedMethods, setAllowedMethods] = useState<string[]>([
    "totp",
    "email",
    "webauthn",
  ]);
  const [trustedDeviceDays, setTrustedDeviceDays] = useState(30);
  const [gracePeriodDays, setGracePeriodDays] = useState(0);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get("/api/agency/mfa-settings");
      setSettings(data);
      setEnforced(data.mfa_enforced || false);
      setAllowedMethods(
        data.mfa_allowed_methods || ["totp", "email", "webauthn"],
      );
      setTrustedDeviceDays(data.mfa_trusted_device_days ?? 30);
      setGracePeriodDays(data.mfa_grace_period_days ?? 0);
    } catch (err) {
      console.error("Error fetching MFA settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleMethodToggle = (method: string) => {
    setAllowedMethods((prev) =>
      prev.includes(method)
        ? prev.filter((m) => m !== method)
        : [...prev, method],
    );
  };

  const handleSave = async () => {
    if (enforced && allowedMethods.length === 0) {
      toast.error(
        "At least one MFA method must be allowed when enforcement is enabled.",
        toastOptions,
      );
      return;
    }

    try {
      setSaving(true);
      await axios.patch("/api/agency/mfa-settings", {
        mfa_enforced: enforced,
        mfa_allowed_methods: allowedMethods,
        mfa_trusted_device_days: trustedDeviceDays,
        mfa_grace_period_days: gracePeriodDays,
      });
      toast.success("MFA settings saved successfully.", toastOptions);
      fetchSettings();
    } catch (err) {
      toast.error("Failed to save MFA settings.", toastOptions);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="p-6 card border border-base-300 bg-base-100 mt-8">
        <p className="text-base-content/60">Loading MFA settings...</p>
      </div>
    );

  const adoptionPercent =
    settings?.officerMfaStats && settings.officerMfaStats.total > 0
      ? Math.round(
          (settings.officerMfaStats.configured /
            settings.officerMfaStats.total) *
            100,
        )
      : 0;

  return (
    <div className="p-6 card border border-base-300 bg-base-100 mt-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-base-300">
        <div>
          <h2 className="text-2xl font-semibold text-base-content mb-2">
            Security & MFA
          </h2>
          <p className="text-base-content/60">
            Configure multi-factor authentication for your agency's officers and
            admins. Required for CJIS compliance.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {settings?.officerMfaStats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="p-4 bg-base-200 rounded-lg border border-base-200">
            <p className="text-sm font-medium text-base-content/60">Total Officers</p>
            <p className="text-2xl font-bold text-base-content mt-1">
              {settings.officerMfaStats.total}
            </p>
          </div>
          <div className="p-4 bg-base-200 rounded-lg border border-base-200">
            <p className="text-sm font-medium text-base-content/60">MFA Configured</p>
            <p className="text-2xl font-bold text-base-content mt-1">
              {settings.officerMfaStats.configured}
            </p>
          </div>
          <div className="p-4 bg-base-200 rounded-lg border border-base-200">
            <p className="text-sm font-medium text-base-content/60">Adoption Rate</p>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-2xl font-bold text-base-content">
                {adoptionPercent}%
              </p>
              <div className="flex-1 h-2 bg-base-300 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    adoptionPercent === 100
                      ? "bg-green-500"
                      : adoptionPercent >= 50
                        ? "bg-blue-500"
                        : "bg-yellow-500"
                  }`}
                  style={{ width: `${adoptionPercent}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enforcement Toggle */}
      <div className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-base-200 rounded-lg border border-base-200">
          <div>
            <h3 className="font-medium text-base-content">
              Enforce MFA for All Officers
            </h3>
            <p className="text-sm text-base-content/60 mt-1">
              When enabled, all officers and admins must set up MFA to access
              the system.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
            <input
              type="checkbox"
              checked={enforced}
              onChange={(e) => setEnforced(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {enforced && (
          <div className="space-y-6 p-6 bg-base-200 rounded-lg border border-base-200">
            {/* Allowed Methods */}
            <div>
              <h3 className="text-gray-800 dark:text-base-content/80 font-medium text-sm mb-3">
                Allowed MFA Methods
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-base-100 rounded-lg border border-base-300">
                  <div>
                    <span className="text-sm font-medium text-base-content">
                      Authenticator App (TOTP)
                    </span>
                    <p className="text-xs text-base-content/60 mt-0.5">
                      Google Authenticator, Microsoft Authenticator, etc.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
                    <input
                      type="checkbox"
                      checked={allowedMethods.includes("totp")}
                      onChange={() => handleMethodToggle("totp")}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-3 bg-base-100 rounded-lg border border-base-300">
                  <div>
                    <span className="text-sm font-medium text-base-content">
                      Device Biometrics (WebAuthn)
                    </span>
                    <p className="text-xs text-base-content/60 mt-0.5">
                      Touch ID, Face ID, Windows Hello, security keys.
                      Phishing-resistant.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
                    <input
                      type="checkbox"
                      checked={allowedMethods.includes("webauthn")}
                      onChange={() => handleMethodToggle("webauthn")}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-3 bg-base-100 rounded-lg border border-base-300">
                  <div>
                    <span className="text-sm font-medium text-base-content">
                      Email Code
                    </span>
                    <p className="text-xs text-base-content/60 mt-0.5">
                      Send a one-time code to the officer's email address.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
                    <input
                      type="checkbox"
                      checked={allowedMethods.includes("email")}
                      onChange={() => handleMethodToggle("email")}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Grace Period & Trusted Device — side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-base-100 rounded-lg border border-base-300">
                <h3 className="text-sm font-medium text-base-content mb-1">
                  Grace Period
                </h3>
                <p className="text-xs text-base-content/60 mb-3">
                  Days officers have to set up MFA after enforcement. 0 =
                  immediate.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={30}
                    value={gracePeriodDays}
                    onChange={(e) =>
                      setGracePeriodDays(parseInt(e.target.value) || 0)
                    }
                    className="w-20 px-3 py-2 border border-gray-300 dark:border-base-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-sm text-base-content/60">days</span>
                </div>
              </div>

              <div className="p-4 bg-base-100 rounded-lg border border-base-300">
                <h3 className="text-sm font-medium text-base-content mb-1">
                  Trusted Device Duration
                </h3>
                <p className="text-xs text-base-content/60 mb-3">
                  How long a device can skip MFA after verification. 0 =
                  disabled.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={90}
                    value={trustedDeviceDays}
                    onChange={(e) =>
                      setTrustedDeviceDays(parseInt(e.target.value) || 0)
                    }
                    className="w-20 px-3 py-2 border border-gray-300 dark:border-base-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-sm text-base-content/60">days</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="pt-2">
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MFAConfiguration;
